import axios from "axios";
import { sessionIdle } from './utils/activity';
import router from './router';
import store from './store';
import { spaceHeader } from './utils/space';

const apiClient = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL,
    withCredentials: true
});

// the space of the switcher (personal or a team, task 59) goes with every request
apiClient.interceptors.request.use(config => {
  const value = spaceHeader(store)['X-Sl-Space'];
  if (config.headers && typeof config.headers.set === 'function') config.headers.set('X-Sl-Space', value);
  else config.headers = { ...(config.headers || {}), 'X-Sl-Space': value };
  return config;
});

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  
  failedQueue = [];
};

apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 403) {
      // @todo подумать как обрабатывать 403 из админки ajax
      console.error('Access Denied - 403 Forbidden');
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Skip token refresh for public endpoints, auth check endpoint, refresh endpoint, and signout
      const publicEndpoints = ['/check', '/auth/refresh', '/signout', '/confirm/', '/verify-email/', '/register', '/login', '/forgot-password', '/reset-password', '/resend-verification'];
      const isPublicEndpoint = publicEndpoints.some(endpoint => originalRequest.url.includes(endpoint));
      
      if (isPublicEndpoint) {
        return Promise.reject(error);
      }
      // minutes without an action: no refresh, the session ends (background polling of a page
      // left alone must not keep it alive)
      if (sessionIdle()) {
        await store.dispatch('logoutIdle');
        if (router.currentRoute.value.path !== '/signin') router.push('/signin');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Set refreshing state
        store.dispatch('handleTokenRefresh', true);
        
        // Attempt to refresh the token
        const refreshResponse = await apiClient.post('/auth/refresh');
        
        // Update user roles from new access token if provided
        if (refreshResponse.data && refreshResponse.data.access_token) {
          await store.dispatch('updateUserRolesFromToken', refreshResponse.data.access_token);
        }
        
        processQueue(null);
        isRefreshing = false;
        store.dispatch('handleTokenRefresh', false);
        
        // Retry original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        store.dispatch('handleTokenRefresh', false);
        
        // Refresh failed, logout user
        console.error('[ApiClient] Token refresh failed - logging out user');
        
        // Execute logout action, then the flag (logout clears it) so sign-in says why
        await store.dispatch('logout');
        store.commit('setTimeOut', true);
        
        // Check if we're on a public page
        const currentPath = router.currentRoute.value.path;
        const publicPaths = ['/signin', '/signup', '/restore-password', '/confirm', '/verify-email', '/reset-password', '/'];
        const isOnPublicPath = publicPaths.some(path => currentPath.startsWith(path));
        
        // Redirect to signin if not already on a public page
        if (!isOnPublicPath && currentPath !== '/signin') {
          console.log('[ApiClient] Redirecting to signin due to failed token refresh');
          router.push('/signin');
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;